// SocialButtons.jsx - Reusable social sign-in buttons component
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useUser } from '../../context/UserContext';
import { ROUTES } from '../../constants/Routes';
import SocialButton from './SocialButton';
import { logUserActivity, recordLoginConversion, setLanguage as apiSetLanguage } from '../../utils/api';
import { supportedLanguageCodes } from '../../i18n';
import { parseLocalePath } from '../../constants/Routes';

// Persist the URL-based locale (from /es/login, /en/signup, etc.) to
// Firestore right after social sign-in so the authenticated app renders
// in the same language the visitor logged in from. Legacy users whose
// Firestore doc still holds the old `en` would otherwise get flipped
// back to English by the getLanguage fetch in UserContext.
const persistUrlLocale = async () => {
  const { locale } = parseLocalePath(window.location.pathname);
  if (!supportedLanguageCodes.includes(locale)) return;
  try {
    await apiSetLanguage(locale);
  } catch (err) {
    console.warn('SocialButtons: setLanguage failed', err);
  }
};

/**
 * SocialButtons component for Google and Apple sign-in
 * @param {Object} props
 * @param {Function} props.onError - Callback to set error message
 * @param {Function} props.isEmailAllowed - Optional function to check if email is allowed (for whitelist)
 * @param {string} props.successRoute - Route to navigate to after successful sign-in (default: BLOOMTERMINAL)
 * @param {'dark'|'light'} props.variant - Surface the buttons sit on, matching AuthLayout
 */
const SocialButtons = ({
  onError,
  isEmailAllowed = null,
  successRoute = '/',
  conversionRef = null,
  variant = 'dark',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithApple } = useUser();

  // Handle sign-in errors
  const handleSignInError = (error, provider) => {
    console.error(`${provider} sign-in error:`, error);
    if (onError) {
      if (error.code === 'auth/popup-closed-by-user') {
        onError(t('errors.signInCancelled'));
      } else if (error.code === 'auth/popup-blocked') {
        onError(t('errors.popupBlocked'));
      } else {
        onError(t('errors.failedSignIn', { provider }));
      }
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    try {
      if (onError) onError('');
      
      const user = await signInWithGoogle();

      // Check email whitelist if provided (for Login page)
      if (isEmailAllowed && user?.email) {
        if (!isEmailAllowed(user.email)) {
          await signOut(auth);
          if (onError) {
            onError(t('errors.accessRestricted'));
          }
          return;
        }
      }

      // Log user login activity
      if (user?.email) {
        logUserActivity(user.email);
        if (conversionRef) await recordLoginConversion(conversionRef);
      }

      await persistUrlLocale();
      navigate(successRoute);
    } catch (error) {
      handleSignInError(error, 'Google');
    }
  };

  // Handle Apple sign-in
  const handleAppleSignIn = async () => {
    try {
      if (onError) onError('');

      const user = await signInWithApple();

      // Check email whitelist if provided (for Login page)
      if (isEmailAllowed && user?.email) {
        if (!isEmailAllowed(user.email)) {
          await signOut(auth);
          if (onError) {
            onError(t('errors.accessRestricted'));
          }
          return;
        }
      }

      // Log user login activity
      if (user?.email) {
        logUserActivity(user.email);
        if (conversionRef) await recordLoginConversion(conversionRef);
      }

      await persistUrlLocale();
      navigate(successRoute);
    } catch (error) {
      handleSignInError(error, 'Apple');
    }
  };

  const GoogleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const AppleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );

  return (
    <div className="space-y-4">
      <SocialButton
        onClick={handleGoogleSignIn}
        provider="Google"
        icon={GoogleIcon}
        variant={variant}
      >
        {t('auth.continueWithGoogle')}
      </SocialButton>

      <SocialButton
        onClick={handleAppleSignIn}
        provider="Apple"
        icon={AppleIcon}
        variant={variant}
      >
        {t('auth.continueWithApple')}
      </SocialButton>
    </div>
  );
};

export default SocialButtons;

